import { useRef } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useOnClickOutside } from "@/hooks/use-on-click-outside";
import { ChipOption } from "./types";

interface StoreFilterChipProps {
  label: string;
  summary: string;
  isOpen: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  onClear: () => void;
  options: ChipOption[];
}

/**
 * Chip de filtro (Estado/Regional). Sem seleção mostra "+ Label"; com seleção mostra
 * "Label: resumo" + X para limpar a dimensão. Clicar no corpo abre/fecha um dropdown
 * FLUTUANTE (absolute, por cima do conteúdo) com multi-select e scroll próprio.
 * Fecha ao clicar fora; "um aberto por vez" é garantido pelo pai.
 */
export function StoreFilterChip({
  label,
  summary,
  isOpen,
  onToggleOpen,
  onClose,
  onClear,
  options,
}: StoreFilterChipProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasSelection = summary.length > 0;

  useOnClickOutside(containerRef, onClose, isOpen);

  return (
    <div ref={containerRef} className="relative inline-flex">
      <div
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors",
          hasSelection
            ? "border-foreground/30 bg-accent text-accent-foreground"
            : "border-dashed text-muted-foreground hover:bg-muted",
        )}
      >
        <button
          type="button"
          onClick={onToggleOpen}
          className="inline-flex items-center gap-1"
        >
          {!hasSelection && <Plus className="h-3 w-3" />}
          <span className="font-medium">{label}</span>
          {hasSelection && (
            <span className="max-w-[140px] truncate font-normal">: {summary}</span>
          )}
          <ChevronDown
            className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")}
          />
        </button>
        {hasSelection && (
          <button
            type="button"
            onClick={onClear}
            className="ml-0.5 rounded-full hover:text-destructive"
            aria-label={`Limpar ${label}`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border bg-popover text-popover-foreground shadow-md">
          {options.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted-foreground">
              Nenhuma opção disponível
            </p>
          ) : (
            <div className="max-h-[220px] overflow-y-auto overscroll-contain">
              <div className="space-y-0.5 p-1.5">
                {options.map(option => (
                  <label
                    key={option.key}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={option.checked}
                      onCheckedChange={option.onToggle}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
